import { faStar, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Node } from '@react-types/shared';
import { uniqBy } from 'lodash';
import {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useListBox, useOption } from 'react-aria';
import { ComboBoxState } from 'react-stately';
import { ActionGroup } from 'src/components/action-group';
import { Button } from 'src/components/button';
import { Direction } from 'src/components/direction';
import { SelectOption } from 'src/components/form/select';
import {
  NetworkPointsRequest,
  useNetworkPoints,
} from 'src/components/form/select/network-point-select/use-network-points';
import {
  NetworkPointsRootRequest,
  useNetworkPointsRoot,
} from 'src/components/form/select/network-point-select/use-network-points-root';
import {
  ComboboxChildrenProps,
  SearchableSingleSelect,
} from 'src/components/form/select/searchable-single-select';
import { Link } from 'src/components/link';
import { Table } from 'src/components/smart-table';
import { Spinner, SpinnerContainer } from 'src/components/spinner-container';
import { Stack } from 'src/components/stack';
import { Tbody, Td, Th, Tr, TrProps } from 'src/components/table';
import { useOptionalMonolithCurrentUser } from 'src/hooks/use-monolith-user';
import { useNotification } from 'src/hooks/use-notifications';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useSearch } from 'src/hooks/use-search';
import { flyoutStyles } from 'src/styles';
import {
  ActiveNetworkPointFilter,
  NetworkPoint,
  NetworkPointsParams,
} from 'src/types/network-points';
import { handeNonPageLoadRequestError } from 'src/utils/axios-config';
import styled from 'styled-components';

type NetworkPointSelectProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  initialSearch?: string;
  disabled?: boolean;
  placeholder?: string;
  initialOpen?: boolean;
  hint?: string;
  onChange?: (value: string | '') => void;
} & (
  | {
      mode?: 'form';
      pending?: undefined;
    }
  | {
      mode: 'search';
      pending: boolean;
    }
);

export function NetworkPointSelect(props: NetworkPointSelectProps) {
  const auth = useOptionalMonolithCurrentUser();
  const {
    name,
    label,
    placeholder,
    stacked,
    inline,
    hideLabel = false,
    disabled,
    initialOpen,
    hint,
    initialSearch = '',
    onChange,
    mode = 'form',
    pending,
  } = props;

  const networkPointsRoot = useNetworkPointsRoot({
    // special error handling case: we'd like to immidiately ask for the network points root,
    // but we'd like to postpone an error notification until someone actually interacts with the search
    onError(error) {
      return error;
    },
  });
  const notify = useNotification();
  const [_, setReauthenticate] = useReauthenticate();
  const firstUserInteraction = useRef(true);

  const isShipper =
    auth?.role === 'SHIPPER_ADMIN' || auth?.role === 'SHIPPER_USER';

  const [params, setParams] = useState<NetworkPointsParams>({
    q: initialSearch,
    onlyFavourites: false,
    favouritesAboveIfInitialSearch: isShipper,
    onlyOwn: false,
    active: ActiveNetworkPointFilter.ACTIVE,
    limit: 20,
    offset: 0,
  });

  const minLength = 0;

  const [searchValue, setSearchValue, searchQuery] = useSearch(initialSearch, {
    minLength,
    delay: 100,
  });

  const networkPoints = useNetworkPoints();

  useEffect(() => {
    if (!networkPointsRoot.response) return;
    networkPoints.execute({ networkPointsRoot, params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkPointsRoot.response, params]);

  useEffect(() => {
    setParams((params) => ({
      ...params,
      favouritesAboveIfInitialSearch: !searchQuery && isShipper,
      q: searchQuery,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const loadMoreNetworkPoints = useNetworkPoints();

  // whenever params change we reset the "load more" request
  useEffect(() => {
    loadMoreNetworkPoints.canceler?.();
    loadMoreNetworkPoints.setResponse(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <SearchableSingleSelect
      mode="server-search"
      name={name}
      label={label}
      placeholder={placeholder}
      stacked={stacked}
      inline={inline}
      disabled={disabled}
      setSearchValue={setSearchValue}
      minSearchValueLength={minLength}
      searchResult={networkPoints.response?.data._embedded.items.map(
        (item) => ({
          label: item.name,
          value: item.id,
          networkPoint: item,
        })
      )}
      searchValue={searchValue}
      keepSearchValue={mode === 'search'}
      keepOpenIfFocused={mode === 'search'}
      lastSearchQuery={networkPoints.response?.config.params.infix ?? ''}
      pending={networkPoints.pending}
      hint={hint}
      hideLabel={hideLabel}
      leftAddon={
        mode === 'search' ? (
          pending ? (
            <Spinner />
          ) : (
            <FontAwesomeIcon icon={faSearch} />
          )
        ) : undefined
      }
      popoverStyle={{ width: '60rem' }}
      children={(props) => (
        <SelectPopover
          {...props}
          params={params}
          setParams={setParams}
          networkPointsRoot={networkPointsRoot}
          networkPoints={networkPoints}
          loadMoreNetworkPoints={loadMoreNetworkPoints}
          mode={mode}
        />
      )}
      sortBy="manual"
      onChange={onChange}
      initialOpen={initialOpen}
      onFocus={() => {
        if (!firstUserInteraction.current) return;
        firstUserInteraction.current = false;
        if (!networkPointsRoot.error) return;
        handeNonPageLoadRequestError({
          notify,
          error: networkPointsRoot.error,
          setReauthenticate,
        });
      }}
    />
  );
}

const Flyout = styled.div`
  ${flyoutStyles}
`;

const TableWrapper = styled.div`
  min-width: 17.9rem;
  max-height: 50vh;
  overflow: auto;
`;

const SelectPopover: FC<
  ComboboxChildrenProps & {
    params: NetworkPointsParams;
    setParams: Dispatch<SetStateAction<NetworkPointsParams>>;
    networkPointsRoot: NetworkPointsRootRequest;
    networkPoints: NetworkPointsRequest;
    loadMoreNetworkPoints: NetworkPointsRequest;
    mode: 'form' | 'search';
  }
> = ({
  searchState,
  comboBoxState: state,
  listBoxOptions,
  listBoxRef,
  name,
  params,
  setParams,
  networkPointsRoot,
  networkPoints,
  loadMoreNetworkPoints,
  mode,
}) => {
  const { listBoxProps } = useListBox(listBoxOptions, state, listBoxRef);

  const options = [...state.collection] as Node<
    SelectOption & { networkPoint: NetworkPoint }
  >[];

  const auth = useOptionalMonolithCurrentUser();
  const isShipper =
    auth?.role === 'SHIPPER_ADMIN' || auth?.role === 'SHIPPER_USER';
  const isTso = auth?.role === 'TSO_USER';

  // merge first network point request with "load more" results
  useEffect(() => {
    if (!networkPoints.response) return;
    if (!loadMoreNetworkPoints.response) return;

    const items = uniqBy(
      [
        ...networkPoints.response.data._embedded.items,
        ...loadMoreNetworkPoints.response.data._embedded.items,
      ],
      'id'
    );

    networkPoints.setResponse({
      ...networkPoints.response,
      data: {
        ...networkPoints.response.data,
        _embedded: { items },
        limit:
          loadMoreNetworkPoints.response.data.offset +
          loadMoreNetworkPoints.response.data.limit,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreNetworkPoints.response]);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // whenever params change we scroll back to the top
  useEffect(() => {
    if (!tableWrapperRef.current) return;
    tableWrapperRef.current.scrollTop = 0;
  }, [params]);

  return (
    <Flyout>
      <Stack justifyItems="end">
        <div style={{ margin: '1rem' }}>
          <ActionGroup>
            <Button
              mode="secondary"
              size="small"
              isActive={
                params.active ===
                  ActiveNetworkPointFilter.ACTIVE_AND_PREVIOUSLY_ACTIVE &&
                !params.onlyFavourites &&
                !params.onlyOwn
              }
              onClick={() =>
                setParams((params) => ({
                  ...params,
                  onlyFavourites: false,
                  onlyOwn: false,
                  active: ActiveNetworkPointFilter.ACTIVE_AND_PREVIOUSLY_ACTIVE,
                }))
              }
              data-testid="nwp-search-show-all"
            >
              Show All
            </Button>

            <Button
              mode="secondary"
              size="small"
              isActive={params.active === ActiveNetworkPointFilter.ACTIVE}
              onClick={() =>
                setParams((params) => ({
                  ...params,
                  onlyFavourites: false,
                  onlyOwn: false,
                  active: ActiveNetworkPointFilter.ACTIVE,
                }))
              }
              data-testid="nwp-search-active-only"
            >
              Active Only
            </Button>

            {isShipper && (
              <Button
                mode="secondary"
                size="small"
                isActive={
                  params.active ===
                    ActiveNetworkPointFilter.ACTIVE_AND_PREVIOUSLY_ACTIVE &&
                  Boolean(params.onlyFavourites)
                }
                onClick={() =>
                  setParams((params) => ({
                    ...params,
                    onlyFavourites: true,
                    onlyOwn: false,
                    active:
                      ActiveNetworkPointFilter.ACTIVE_AND_PREVIOUSLY_ACTIVE,
                  }))
                }
                data-testid="nwp-search-favorites-only"
              >
                <FontAwesomeIcon icon={faStar} /> Favourites Only
              </Button>
            )}

            {isTso && (
              <Button
                mode="secondary"
                size="small"
                isActive={
                  params.active ===
                    ActiveNetworkPointFilter.ACTIVE_AND_OWN_AND_PREVIOUSLY_ACTIVE &&
                  Boolean(params.onlyOwn)
                }
                onClick={() =>
                  setParams((params) => ({
                    ...params,
                    onlyFavourites: false,
                    onlyOwn: true,
                    active:
                      ActiveNetworkPointFilter.ACTIVE_AND_OWN_AND_PREVIOUSLY_ACTIVE,
                  }))
                }
                data-testid="nwp-search-own-only"
              >
                Own Only
              </Button>
            )}
          </ActionGroup>
        </div>
      </Stack>

      <SpinnerContainer
        pending={searchState.case === 'PENDING'}
        data-testid="nwp-search-spinner-container"
      >
        <TableWrapper
          ref={tableWrapperRef}
          data-testid="nwp-search-results-scroll-container"
          onScroll={(event) => {
            // when we scroll to the bottom we try to load more network points
            if (!networkPointsRoot.response) return;
            if (!networkPoints.response) return;
            if (networkPoints.pending) return;
            if (loadMoreNetworkPoints.pending) return;

            const element = event.currentTarget;
            const offset = 100;
            const scrollPos = element.scrollHeight - element.scrollTop - offset;
            const reachedBottom = scrollPos < element.clientHeight;

            const data =
              loadMoreNetworkPoints.response?.data ??
              networkPoints.response.data;
            const moreToLoad = data.offset + data.limit < data.total;

            if (!reachedBottom || !moreToLoad) return;

            loadMoreNetworkPoints.execute({
              networkPointsRoot,
              params: { ...params, offset: data.offset + data.limit },
            });
          }}
        >
          <Table
            data-testid="nwp-search-table"
            top="1px"
            data={options}
            setId={(item) => item.key}
            body={({ tbodyProps }) => (
              <Tbody
                {...tbodyProps}
                {...listBoxProps}
                ref={listBoxRef as RefObject<HTMLTableSectionElement>}
              />
            )}
            row={(props) => (
              <SelectRow {...props} state={state} name={name} mode={mode} />
            )}
            cols={[
              {
                key: 'networkPoint',
                width: 3,
                head: <Th>Network Point</Th>,
                body: (item) => (
                  <Td>
                    {isShipper && item.value.networkPoint.favourite && (
                      <>
                        <FontAwesomeIcon icon={faStar} />{' '}
                      </>
                    )}
                    {item.value.networkPoint.name}

                    <br />

                    {item.value.networkPoint.bundle ? (
                      <Direction
                        direction="BUNDLE"
                        marketAreaName={[
                          item.value.networkPoint.exit.marketer,
                          item.value.networkPoint.entry.marketer,
                        ]}
                      />
                    ) : (
                      <Direction
                        direction={item.value.networkPoint.direction}
                        marketAreaName={item.value.networkPoint.marketer}
                      />
                    )}

                    {!item.value.networkPoint.active && (
                      <>
                        {' '}
                        <small style={{ color: '#7c7c7c', fontWeight: 'bold' }}>
                          Inactive
                        </small>
                      </>
                    )}
                  </Td>
                ),
              },
              {
                key: 'marketer',
                width: 3,
                head: <Th>TSO</Th>,
                body: (item) => (
                  <Td>
                    {item.value.networkPoint.bundle ? (
                      <>
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.value.networkPoint.exit.marketerId}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.value.networkPoint.exit.marketer}
                        </Link>{' '}
                        /{' '}
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.value.networkPoint.entry.marketerId}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {item.value.networkPoint.entry.marketer}
                        </Link>
                      </>
                    ) : (
                      <Link
                        mode="default-underlined"
                        to={`/market-information/players/operators/${item.value.networkPoint.marketerId}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.value.networkPoint.marketer}
                      </Link>
                    )}
                  </Td>
                ),
              },
              {
                key: 'id',
                width: 2,
                head: <Th>ID</Th>,
                body: (item) => (
                  <Td aria-label={item.textValue}>
                    {item.value.networkPoint.identifier}
                  </Td>
                ),
              },
            ]}
            empty={
              searchState.case === 'EMPTY' && searchState.lastSearchQuery
                ? {
                    label: `Could not find network points for "${searchState.lastSearchQuery}".`,
                  }
                : searchState.case === 'EMPTY' && !searchState.lastSearchQuery
                ? {
                    // e.g. favorite filter selected, but no favorite configured
                    label: 'Could not find network points.',
                  }
                : {
                    // e.g. the previous search _was_ empty, but now have a pending state for a new request
                    label: 'Could not find network points.',
                  }
            }
          />
          {loadMoreNetworkPoints.pending && (
            <Stack
              justifyContent="center"
              style={{ margin: '2rem' }}
              data-testid="nwp-search-loading-more"
            >
              <Spinner />
            </Stack>
          )}
        </TableWrapper>
      </SpinnerContainer>
    </Flyout>
  );
};

const SelectRow: FC<{
  item: Node<SelectOption<string | null>>;
  state: ComboBoxState<SelectOption<string | null>>;
  name: string;
  trProps: TrProps;
  mode: 'form' | 'search';
}> = ({ item, state, name, trProps, mode = 'form' }) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const { optionProps, isSelected, isFocused } = useOption(
    { key: item.key },
    state,
    ref
  );

  return (
    <Tr
      {...trProps}
      {...optionProps}
      onPointerUp={(event) => {
        if (event.target instanceof HTMLAnchorElement) return;
        optionProps.onPointerUp?.(event);
      }}
      onPointerLeave={(event) => {
        state.selectionManager.setFocusedKey(null);
        optionProps.onPointerLeave?.(event);
      }}
      // within search mode we don't visualize the selection
      isSelected={mode === 'search' ? false : isSelected}
      ref={ref}
      isFocused={isFocused}
      data-testid={`${name}-option-${item.value.value}`}
    />
  );
};
